import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import PageBanner from '../components/PageBanner';
import '../styles/GDPRInformation.css';
import EnhancedHeader from '../components/EnhancedHeader';


const GDPRInformation = () => {
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>GDPR Information | Active Care Residential</title>
        <meta name="description" content="GDPR Information and your data rights at Active Care Residential." />
        <meta name="keywords" content="GDPR, data protection, data rights" />
      </Helmet>

      {/* Page Banner */}
      <EnhancedHeader
        title="GDPR Information"
        subtitle="Your data rights and protections"
        backgroundImage="/images/residential-care-banner.jpg"
        backgroundGradient="linear-gradient(135deg, rgba(11, 180, 170, 0.7) 0%, rgba(56, 239, 125, 0.7) 100%)"
      />


      <motion.section
        className="gdpr-page-wrapper"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="gdpr-page-section">
          <div className="gdpr-page-container">
            <div className="gdpr-page-body">
              <section className="gdpr-page-item">
                <h2>What is GDPR?</h2>
                <p>
                  The General Data Protection Regulation (GDPR) is a European Union regulation that protects the privacy and personal data of individuals. In the UK, GDPR is complemented by the Data Protection Act 2018. These laws give you rights over your personal information and place obligations on organizations like Active Care Residential to handle your data responsibly.
                </p>
              </section>

              <section className="gdpr-page-item">
                <h2>Our GDPR Commitment</h2>
                <p>
                  Active Care Residential is fully committed to GDPR compliance. We have implemented robust procedures and safeguards to ensure:
                </p>
                <ul className="gdpr-page-list">
                  <li>Your personal data is processed lawfully, fairly, and transparently</li>
                  <li>Data is collected only for specified, explicit, and legitimate purposes</li>
                  <li>Data is kept accurate, complete, and up-to-date</li>
                  <li>Data is kept secure and protected from unauthorized access</li>
                  <li>Data is retained only for as long as necessary</li>
                  <li>Your rights are respected and enforced</li>
                </ul>
              </section>

              <section className="gdpr-page-item">
                <h2>Your Data Rights Under GDPR</h2>
                <p>
                  You have the following rights regarding your personal data:
                </p>

                <div className="gdpr-page-right-item">
                  <h3>1. Right of Access</h3>
                  <p>
                    You have the right to access your personal data and receive a copy of the information we hold about you. We will provide this within 30 days of your request (extendable by 60 days for complex requests).
                  </p>
                </div>

                <div className="gdpr-page-right-item">
                  <h3>2. Right to Rectification</h3>
                  <p>
                    If your personal data is inaccurate or incomplete, you have the right to request correction or completion. We will rectify the data and inform relevant recipients within 30 days.
                  </p>
                </div>

                <div className="gdpr-page-right-item">
                  <h3>3. Right to Erasure (Right to be Forgotten)</h3>
                  <p>
                    You have the right to request deletion of your personal data in certain circumstances, such as when it is no longer necessary for its original purpose. We will delete your data and inform relevant recipients within 30 days, unless legal obligations require retention.
                  </p>
                </div>

                <div className="gdpr-page-right-item">
                  <h3>4. Right to Restrict Processing</h3>
                  <p>
                    You may request that we restrict processing of your personal data while we verify its accuracy or consider your objection to processing.
                  </p>
                </div>

                <div className="gdpr-page-right-item">
                  <h3>5. Right to Data Portability</h3>
                  <p>
                    You have the right to receive your personal data in a structured, commonly used, and machine-readable format, and to transmit it to another organization if possible.
                  </p>
                </div>

                <div className="gdpr-page-right-item">
                  <h3>6. Right to Object</h3>
                  <p>
                    You have the right to object to processing of your personal data, including for direct marketing purposes. We will cease processing upon receipt of your objection.
                  </p>
                </div>

                <div className="gdpr-page-right-item">
                  <h3>7. Rights Related to Automated Decision-Making</h3>
                  <p>
                    You have the right not to be subject to decisions based solely on automated processing that produces legal or similarly significant effects concerning you.
                  </p>
                </div>
              </section>

              <section className="gdpr-page-item">
                <h2>How to Exercise Your Rights</h2>
                <p>
                  To exercise any of your rights, please submit a written request to our Data Protection Officer at:
                </p>
                <div className="gdpr-page-contact-box">
                  <p><strong>Data Protection Officer</strong></p>
                  <p><strong>Active Care Residential</strong></p>
                  <p>Email: info@activecareresidential.co.uk</p>
                  <p>Phone: +44 7737 207521</p>
                  <p>Address: East Ham, London</p>
                </div>
                <p>
                  Please provide sufficient information to identify yourself and specify which right you wish to exercise. We will respond to your request within 30 days.
                </p>
              </section>

              <section className="gdpr-page-item">
                <h2>Data Processing Activities</h2>
                <p>
                  We process your personal data for the following main purposes:
                </p>
                <ul className="gdpr-page-list">
                  <li>Providing care services and support</li>
                  <li>Managing your account and payments</li>
                  <li>Complying with legal and regulatory obligations (CQC, safeguarding, health and safety)</li>
                  <li>Communicating with you about your care</li>
                  <li>Protecting the health and safety of our residents and staff</li>
                  <li>Improving our services and facilities</li>
                  <li>Marketing and promotional communications (with your consent)</li>
                </ul>
              </section>

              <section className="gdpr-page-item">
                <h2>Data Retention</h2>
                <p>
                  We retain your personal data for different periods depending on the type of data and the context:
                </p>
                <ul className="gdpr-page-list">
                  <li><strong>Care Records:</strong> Minimum 8 years after discharge (as per NHS guidelines)</li>
                  <li><strong>Financial Records:</strong> 7 years for tax purposes</li>
                  <li><strong>Communication Records:</strong> 3 years or until service termination</li>
                  <li><strong>Marketing Data:</strong> Until consent is withdrawn</li>
                  <li><strong>Website Analytics:</strong> 2 years</li>
                </ul>
              </section>

              <section className="gdpr-page-item">
                <h2>Data Transfers</h2>
                <p>
                  Your data is primarily processed and stored within the United Kingdom. If we need to transfer data outside the UK or EEA, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses or adequacy decisions.
                </p>
              </section>

              <section className="gdpr-page-item">
                <h2>Data Protection Impact Assessments</h2>
                <p>
                  We conduct Data Protection Impact Assessments (DPIA) for any processing activities that involve high risk to your rights and freedoms. This helps us identify and mitigate potential privacy risks.
                </p>
              </section>

              <section className="gdpr-page-item">
                <h2>Complaint Rights</h2>
                <p>
                  If you believe we have violated your data protection rights, you have the right to lodge a complaint with the Information Commissioner's Office (ICO):
                </p>
                <div className="gdpr-page-contact-box">
                  <p><strong>Information Commissioner's Office (ICO)</strong></p>
                  <p>Website: www.ico.org.uk</p>
                  <p>Phone: 0303 123 1113</p>
                  <p>Post: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF</p>
                </div>
              </section>

              <section className="gdpr-page-item">
                <h2>Changes to This Policy</h2>
                <p>
                  We may update this GDPR Information document periodically to reflect changes in our processing activities or regulatory requirements. We will notify you of significant changes.
                </p>
              </section>
            </div>
          </div>
        </div>
      </motion.section>
    </>
  );
};

export default GDPRInformation;